import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

export type AvatarRole = 'admin' | 'sub_admin' | 'employee' | 'super_user';

@Component({
  selector: 'app-avatar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './avatar.html',
  styleUrl: './avatar.scss'
})
export class Avatar implements OnInit, OnChanges {
  /** Who is this avatar for — drives whether we show a company logo or a personal image/initials */
  @Input() role: AvatarRole = 'employee';

  /** Employee's own uploaded profile image (used when role === 'employee') */
  @Input() profileImageUrl: string | null = null;

  /** Company / sub-company logo (used when role is 'admin' or 'sub_admin') */
  @Input() companyLogoUrl: string | null = null;

  /** Only used for employees — ignored for admin/sub_admin, since a logo has no name */
  @Input() firstName?: string;
  @Input() lastName?: string;

  /** Manual override if a caller wants to force specific initials instead of deriving them */
  @Input() initials?: string;

  @Input() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number = 'md';
  @Input() shape: 'circle' | 'rounded' | 'square' = 'circle';
  @Input() status?: 'online' | 'offline' | 'away' | 'busy';
  @Input() customBgColor?: string;
  @Input() customTextColor?: string;

  /** Optional override; otherwise derived from role ("Company Logo" vs "User") */
  @Input() alt?: string;

  hasImageError = false;
  computedInitials = '';
  colorIndex = 1;

  get isCompanyLogoRole(): boolean {
    return this.role === 'admin' || this.role === 'sub_admin';
  }

  /** Single source of truth for which URL should be rendered, based on role */
  get resolvedSrc(): string | null {
    return this.isCompanyLogoRole ? this.companyLogoUrl : this.profileImageUrl;
  }

  get resolvedAlt(): string {
    return this.alt ?? (this.isCompanyLogoRole ? 'Company Logo' : 'User');
  }

  get showImage(): boolean {
    return !!this.resolvedSrc && !this.hasImageError;
  }

  ngOnInit(): void {
    this.updateAvatar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Any change that could affect which image is shown should clear a previous error state
    if (changes['profileImageUrl'] || changes['companyLogoUrl'] || changes['role']) {
      this.hasImageError = false;
    }
    this.updateAvatar();
  }

  private updateAvatar(): void {
    // Company logo roles never fall back to initials — just the image or the default icon
    this.computedInitials = this.isCompanyLogoRole ? '' : this.getInitials();
    this.colorIndex = this.calculateColorIndex(this.computedInitials || this.firstName || this.lastName || '');
  }

  private getInitials(): string {
    if (this.initials) {
      return this.initials.trim().toUpperCase();
    }

    let computed = '';
    if (this.firstName) {
      computed += this.firstName.trim().charAt(0);
    }
    if (this.lastName) {
      computed += this.lastName.trim().charAt(0);
    }

    if (!computed && this.firstName) {
      const parts = this.firstName.trim().split(/\s+/);
      if (parts.length > 1) {
        computed = parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
      } else {
        computed = this.firstName.trim().substring(0, Math.min(2, this.firstName.trim().length));
      }
    }

    return computed.toUpperCase();
  }

  private calculateColorIndex(seed: string): number {
    if (!seed) return 1;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % 8;
    return index + 1;
  }

  onImageError(): void {
    this.hasImageError = true;
  }

  get isPresetSize(): boolean {
    return typeof this.size === 'string';
  }

  get customSizeStyle(): any {
    if (this.isPresetSize) {
      return {};
    }
    const sizePx = `${this.size}px`;
    const fontSize = `${(this.size as number) * 0.4}px`;
    return {
      width: sizePx,
      height: sizePx,
      'font-size': fontSize,
      'line-height': sizePx
    };
  }

  get avatarClass(): string {
    const classes: string[] = ['avatar-container'];

    if (this.isPresetSize) {
      classes.push(`avatar-${this.size}`);
    }

    classes.push(`avatar-${this.shape}`);

    if (!this.showImage) {
      classes.push(`avatar-bg-${this.colorIndex}`);
    }

    return classes.join(' ');
  }
}